import React, { useContext, useEffect, useState } from "react";
import { Box, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { useParams } from "react-router-dom";
import {
  GetProjectsResponse,
  GetShareAccessTokenResponse,
  Project,
  Share,
  ShareType,
} from "../../proto/api_pb";
import {
  getShareAccessToken,
  increaseShareUrlViews,
} from "../../services/shareService";
import { getAllProjects } from "../../services/projectService";
import { ContainerForGrid } from "../../components/Exolore/styles";
import { SingleProject } from "../../components/SingleProject/SingleProject";
import { styles } from "../Projects/styles";
import { RpcError } from "grpc-web";
import { toast } from "react-toastify";
import { AuthContext, AuthContextInterface } from "../../contexts";
import { TalkJsContext, TalkJsContextInterface } from "../../contexts";

export const SharedProjectsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [share, setShare] = useState<Share>();
  const [shareToken, setShareToken] = useState<string>();
  const [allowDownload, setAllowDownload] = useState<boolean>(false);
  const [isMaxViewsReached, setIsMaxViewsReached] = useState<boolean>(false);
  const { id } = useParams();
  const isMobileScreen = useMediaQuery("(max-width:900px)");
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const talkJsContext = useContext<TalkJsContextInterface>(TalkJsContext);
  const [session, setSession] = useState<any>(null);
  const [inbox, setInbox] = useState<any>(null);

  useEffect(() => {
    const fetchShareData = async () => {
      if (!id) return;

      try {
        const response = await getShareAccessToken(id);
        if (response instanceof GetShareAccessTokenResponse) {
          const shareData = response.getShare();
          if (shareData) {
            setShare(shareData);
            setShareToken(response.getAccessToken());
            setAllowDownload(shareData.getAllowDownload());

            // Check if max views reached
            if (
              !shareData.getUnlimitedViews() &&
              shareData.getImpressionCount() >= shareData.getMaxViews()
            ) {
              setIsMaxViewsReached(true);
              toast.error("This share link has reached its maximum view limit");
              return;
            }

            // Increment impression count
            await increaseShareUrlViews(id, response.getAccessToken());

            // Fetch projects using the share token
            await fetchProjects(response.getAccessToken(), shareData);
          }
        } else if (response instanceof RpcError) {
          toast.error("Failed to load shared projects");
        }
      } catch (error) {
        console.error("Error fetching share data:", error);
        toast.error("Failed to load shared projects");
      } finally {
        setIsLoading(false);
      }
    };

    fetchShareData();
  }, [id]);

  const fetchProjects = async (token: string, shareData: Share) => {
    const shareType = shareData.getShareType() as number;
    const objectId = shareData.getObjectId();
    console.log("Share type:", shareType, "Object ID:", objectId);
    
    // Check if this is a projects collection share
    // ShareType 5 = SHARE_TYPE_PROJECTS_COLLECTION
    // Also check if object_id contains commas (indicates multiple projects)
    const isProjectsCollection = shareType === 5 || (objectId && objectId.includes(","));
    
    if (!isProjectsCollection) {
      console.error("Invalid share type:", shareType, "Expected: 5 (SHARE_TYPE_PROJECTS_COLLECTION)");
      toast.error("Invalid share type");
      return;
    }

    const projectIds = shareData.getObjectId().split(",").map(id => id.trim()).filter(Boolean);
    console.log("Project IDs from share:", projectIds);

    if (projectIds.length === 0) {
      console.error("No project IDs found in share object");
      toast.error("No projects found in share");
      return;
    }

    try {
      const response = await getAllProjects(
        token,
        shareData.getOwnerId(),
        "",
        undefined,
        "",
        0,
        0,
        "",
        ""
      );

      if (response instanceof GetProjectsResponse) {
        const projectsList = response.getProjectsList();
        console.log("Projects received before filtering:", projectsList.length, projectsList.map(p => p.getId()));
        
        // Filter projects to only include those in the share's project IDs
        // This is a fallback in case the backend doesn't filter correctly
        const filteredProjects = projectsList.filter(project => 
          projectIds.includes(project.getId())
        );
        
        console.log("Projects after filtering:", filteredProjects.length, filteredProjects.map(p => p.getId()));
        setProjects(filteredProjects);
      } else if (response instanceof RpcError) {
        console.error("RPC Error:", response);
        toast.error("Failed to load projects");
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isMaxViewsReached) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography>This share link has reached its maximum view limit.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: "100vh", display: "flex" }}>
        <Box sx={styles.rightSideContainer}>
          <Box
            sx={{
              ...styles.projectsHeaderContainer,
              width: isMobileScreen ? "85vw" : "100%",
            }}
          >
            <Box sx={styles.headerContainer}>
              <Typography sx={{ fontWeight: "bold", fontSize: "30px" }}>
                {share?.getTrackTitle() || "Shared Projects"}
              </Typography>
            </Box>
          </Box>
          <ContainerForGrid
            sx={
              isMobileScreen ? { marginBottom: "90px" } : { marginTop: "15px" }
            }
          >
            {projects.map((project, index) => (
              <SingleProject
                key={index}
                index={index}
                project={project}
                session={session}
                setInbox={setInbox}
                handleUseEffect={async () => {}}
                openFoldersList={[]}
                setOpenFoldersList={() => {}}
                isReorderAvailable={false}
                handleDragStart={() => {}}
                handleDrop={() => {}}
                setDraggedIndex={() => {}}
              />
            ))}
          </ContainerForGrid>
          {projects.length === 0 && !isLoading && (
            <Box sx={styles.emptyMessageContainer}>
              <Typography>No projects found!</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

